import React, { useEffect, useRef, useState, useImperativeHandle } from "react";

import { cn } from "@/lib/utils";

import {
  setSelectedEditorControl,
  syncSelectedControlWithSelection
} from "@/helpers/editor/command-style";

import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";

import ToolbarContextMenu from "./context-menu";

interface Props {
  onEditorChange?: (value: string) => void;
  value?: string;
}

const Editor = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<React.ComponentProps<"div"> & Props>
>(({ onEditorChange, value = "", className, children, ...props }, ref) => {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [rightClickedElement, setRightClickedElement] =
    useState<HTMLElement | null>(null);

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

  const handleEditorChange = (e: React.FormEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerHTML;
    if (onEditorChange && newValue !== value) {
      onEditorChange(newValue);
    }
  };

  useEffect(() => {
    if (innerRef.current && innerRef.current.innerHTML !== value) {
      innerRef.current.innerHTML = value;
    }
  }, [value]);

  useEffect(() => {
    const handleSelectionChange = () => {
      syncSelectedControlWithSelection(innerRef.current);
    };

    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []);

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    const targetElement = e.target as HTMLElement;

    setSelectedEditorControl(innerRef.current, targetElement);

    const iconElement = targetElement.closest("i");
    const tableElement = targetElement.closest("table");
    const buttonElement = targetElement.closest("button");
    const chartElement = targetElement.closest('[data-type="chart"]');
    const imageElement =
      targetElement.closest('[data-type="image"]') ||
      targetElement.closest("img");

    setRightClickedElement(
      (iconElement ||
        tableElement ||
        buttonElement ||
        chartElement ||
        imageElement ||
        targetElement) as HTMLElement
    );
  };

  return (
    <div
      className={cn("relative bg-white [&_div]:align-middle", className)}
      {...props}
    >
      <ContextMenu dir="rtl">
        <ContextMenuTrigger>
          <div
            ref={innerRef}
            contentEditable
            onMouseDownCapture={(e) => {
              setSelectedEditorControl(innerRef.current, e.target);
            }}
            onClickCapture={(e) => {
              setSelectedEditorControl(innerRef.current, e.target);
            }}
            onInput={handleEditorChange}
            onContextMenu={handleContextMenu}
            className="w-full focus-within:outline-none"
          />
        </ContextMenuTrigger>
        <ToolbarContextMenu
          editorRef={innerRef}
          rightClickedElement={rightClickedElement}
        />
        {children}
      </ContextMenu>
      <div
        className="absolute bottom-0 left-0 size-12"
        style={{
          background:
            "linear-gradient(45deg, rgba(248,248,251,1) 50%, rgba(200,200,200,1) 50%)"
        }}
      />
    </div>
  );
});

export default Editor;
